import { LocationStrategy, PathLocationStrategy, registerLocaleData } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import localeEs from '@angular/common/locales/es';
import localeZh from '@angular/common/locales/zh';
import {
	APP_INITIALIZER,
	type ApplicationConfig,
	importProvidersFrom,
	isDevMode,
	LOCALE_ID,
	provideZoneChangeDetection,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { ErrorToastInterceptor } from '@interceptors/error-toast.interceptor';
// Guards are now functions, no need to import classes
import { AuthInterceptor } from '@interceptors/http-interceptor';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import { AuthService } from '@services/auth/auth.service';
import { ConfigService } from '@services/configuration/config.service';
import { LanguageInitService } from '@services/domain/language-init.service';
import { intlLocaleOf } from '@shared/utils/locale.util';

import primelocaleEn from 'primelocale/en.json';
import { ConfirmationService, MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { TranslocoHttpLoader } from './transloco-loader';

// Angular's `date`/`number`/`currency` pipes read LOCALE_ID, NOT Transloco. With
// no LOCALE_ID provided they fall back to en-US, which is why the portal printed
// "5 Aug · 16:32" inside an otherwise Spanish UI while the admin screens (which
// build their own Intl.DateTimeFormat from the active lang) printed "5 ago".
// Registering the locale data and deriving LOCALE_ID from Transloco makes both
// paths agree. Resolved after the language APP_INITIALIZER has run, so it sees
// the user's real language and not the default.
registerLocaleData(localeEs);
registerLocaleData(localeZh);

/**
 * Aura ships with emerald as its primary, so every PrimeNG control painted with
 * `--p-primary-*` — select focus rings, checkboxes, radios, sliders — came out
 * GREEN across the whole app while our own tokens (`--mk-*`) are gold. The
 * ramp is built around `--mk-gold` (#b8933f), with `--mk-gold-deep` at 600 and
 * `--mk-gold-bright` at 400, so a focused field matches the screen around it.
 */
const KairosGold = definePreset(Aura, {
	semantic: {
		primary: {
			50: '#fdfaf1',
			100: '#faf3e2',
			200: '#f2eddc',
			300: '#e3d3a6',
			400: '#d9b452',
			500: '#b8933f',
			600: '#8a6b1f',
			700: '#7a6216',
			800: '#5c4712',
			900: '#463610',
			950: '#2b210a',
		},
		colorScheme: {
			light: {
				primary: {
					color: '#b8933f',
					contrastColor: '#f6f2e8',
					hoverColor: '#8a6b1f',
					activeColor: '#7a6216',
				},
				highlight: {
					background: '#f7f0dd',
					focusBackground: '#f2eddc',
					color: '#8a6b1f',
					focusColor: '#5c4712',
				},
				// Only `primary` was remapped, so every PrimeNG form control still
				// painted itself with Aura's slate ramp: a `p-select` sat next to a
				// plain `.field-input` with a blue-grey border (#cbd5e1), slate text
				// (#334155) and slate icons, inside a warm cream card. Same control,
				// two palettes, on the same row of the same form.
				//
				// These read the `--mk-*` tokens instead of copying their values, so
				// `theme-styles.scss` stays the single source of truth (the tokens
				// and these are both emitted on `:root`, so `var()` resolves).
				formField: {
					background: 'var(--mk-surface)',
					disabledBackground: 'var(--mk-bg-2)',
					filledBackground: 'var(--mk-surface-2)',
					filledHoverBackground: 'var(--mk-surface-2)',
					filledFocusBackground: 'var(--mk-surface-2)',
					borderColor: 'var(--mk-line)',
					hoverBorderColor: 'var(--mk-line-3)',
					color: 'var(--mk-ink)',
					disabledColor: 'var(--mk-muted)',
					placeholderColor: 'var(--mk-muted-3)',
					floatLabelColor: 'var(--mk-text-3)',
					iconColor: 'var(--mk-text-3)',
				},
				// The panel a select opens is a separate element (`appendTo="body"`),
				// so it needs the same treatment or the list drops out of the palette
				// the moment it opens.
				overlay: {
					select: {
						background: 'var(--mk-surface)',
						borderColor: 'var(--mk-line)',
						color: 'var(--mk-ink)',
					},
					popover: {
						background: 'var(--mk-surface)',
						borderColor: 'var(--mk-line)',
						color: 'var(--mk-ink)',
					},
				},
			},
		},
		// Aura rounds form fields at 6px while the Merit inputs are 8px — enough
		// of a difference to read as two different controls side by side.
		formField: {
			borderRadius: '8px',
		},
	},
});

export const appConfig: ApplicationConfig = {
	providers: [
		provideZoneChangeDetection({ eventCoalescing: true }),
		provideRouter(routes, withComponentInputBinding()),
		importProvidersFrom(BrowserModule, FormsModule, ReactiveFormsModule),
		// El de errores va DESPUÉS del de auth: así un 401 que el refresh
		// recupera no llega a enseñar toast, y solo se avisa de lo que de
		// verdad falló.
		provideHttpClient(withFetch(), withInterceptors([AuthInterceptor, ErrorToastInterceptor])),
		provideAnimations(),

		providePrimeNG({
			theme: {
				preset: KairosGold,
				options: {
					prefix: 'p',
					darkModeSelector: 'none',
					cssLayer: false,
				},
			},
			ripple: true,
			translation: primelocaleEn.en,
		}),

		{ provide: LocationStrategy, useClass: PathLocationStrategy },
		{
			provide: LOCALE_ID,
			useFactory: (transloco: TranslocoService) => intlLocaleOf(transloco.getActiveLang()),
			deps: [TranslocoService],
		},
		MessageService,
		AuthService,
		ConfirmationService,
		DynamicDialogRef,
		DynamicDialogConfig,
		DialogService,
		LanguageInitService,
		{
			provide: APP_INITIALIZER,
			useFactory: (configService: ConfigService) => () => configService.loadConfig().toPromise(),
			deps: [ConfigService],
			multi: true,
		},
		{
			/**
			 * Los permisos, al día ANTES de que el router resuelva la primera ruta
			 * (12/09).
			 *
			 * El guard de cada pantalla decide con la lista que hay guardada en el
			 * navegador, y esa lista se escribía solo al iniciar sesión: al recargar
			 * una URL se decidía con una foto que podía ser de días antes, y la
			 * persona acababa en el inicio sin una palabra (punto 9 de la clienta).
			 *
			 * Bloquea el arranque a propósito, como las otras dos: es UNA petición,
			 * y decidir el acceso con datos viejos cuesta más que esos milisegundos.
			 * Al ir antes de que se cree el layout, el menú también se pinta con los
			 * permisos frescos sin tocar el sidebar. Si la llamada falla, sigue
			 * adelante con lo que hubiera: no bloquea la aplicación.
			 */
			provide: APP_INITIALIZER,
			useFactory: (authService: AuthService) => () => authService.refreshCurrentUser().toPromise(),
			deps: [AuthService],
			multi: true,
		},
		{
			provide: APP_INITIALIZER,
			useFactory: (languageInitService: LanguageInitService) => () => languageInitService.initializeLanguage(),
			deps: [LanguageInitService],
			multi: true,
		},
		provideTransloco({
			config: {
				// `zh` is gone: assets/i18n/zh.json was an empty object, so the
				// locale existed in the picker's model with zero translations
				// behind it and everything fell through to the fallback.
				availableLangs: ['es', 'en'],
				defaultLang: 'en',
				fallbackLang: 'en',
				reRenderOnLangChange: true,
				prodMode: !isDevMode(),
				missingHandler: {
					logMissingKey: false,
					useFallbackTranslation: true,
					allowEmpty: false,
				},
			},
			loader: TranslocoHttpLoader,
		}),

		// Service Worker for PWA - Production only
		provideServiceWorker('ngsw-worker.js', {
			enabled: environment.production,
			registrationStrategy: 'registerWhenStable:30000',
		}),
	],
};
